import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import { useQuery } from "@apollo/client";
import { useHistory, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import { useStyles } from "./styles";
import {
  handleChangeRowsPerPage,
  handleCloseSnackbar,
  handleChangePage,
} from "./helpers";
import FilterComponent from "shared/molecules/Filter";
import FilterInputs from "./filter/FilterInputs";
import {
  GET_ALL_EXAM_PAPERS,
  GET_ALL_EXAM_PAPERS_QUESTIONS,
} from "utils/graphql/services/ai-evaluation/queries";
import autoGradeClient from "utils/graphql/autoGradeClient";
import { CircularProgress, Tooltip } from "@mui/material";

type MuiAlertProps = {
  onClose: () => void;
  severity?: "error" | "info" | "success" | "warning" | undefined;
  children?: React.ReactNode;
};

const Alert = React.forwardRef<HTMLDivElement, MuiAlertProps>((props, ref) => {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export function ViewExamPaperQuestions() {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [isFilter, setIsFilter] = useState(false);
  const [title, setTitle] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTill, setCreatedTill] = useState("");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTill, setUpdatedTill] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data,
    loading,
    error,
    refetch: gamesRefetch,
  } = useQuery(GET_ALL_EXAM_PAPERS_QUESTIONS, {
    client: autoGradeClient,
    variables: { input: { id: parseInt(id) } },
    fetchPolicy: "network-only",
  });

  const handleApplyFilter = () => {
    gamesRefetch({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      game_search: title,
      createdFrom,
      createdTill,
      updatedFrom,
      updatedTill,
    });
    setIsFilter(false);
  };

  const handleClearFilter = () => {
    setTitle("");
    setCreatedFrom("");
    setCreatedTill("");
    setUpdatedFrom("");
    setUpdatedTill("");
    gamesRefetch({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      game_search: "",
    });
    setIsFilter(false);
  };

  const examPaper = data ? data?.getExamPaperQuestions?.examPaperQuestions : [];
  const totalCount =
    data?.getExamPaperQuestions?.totalRecords ?? examPaper.length;

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12}>
        <Paper elevation={3} className={classes.paper}>
          <Box display="flex" alignItems="center" justifyContent="end">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => history.goBack()}
            >
              Close
            </Button>
          </Box>
          <Grid item mt={2}>
            <Box display="flex" justifyContent="flex-end">
              <FilterComponent isFilter={isFilter} setIsFilter={setIsFilter}>
                <div>
                  <FilterInputs
                    title={title}
                    setTitle={setTitle}
                    createdFrom={createdFrom}
                    setCreatedFrom={setCreatedFrom}
                    createdTill={createdTill}
                    setCreatedTill={setCreatedTill}
                    updatedFrom={updatedFrom}
                    setUpdatedFrom={setUpdatedFrom}
                    updatedTill={updatedTill}
                    setUpdatedTill={setUpdatedTill}
                    handleApplyFilter={handleApplyFilter}
                    handleClearFilter={handleClearFilter}
                  />
                </div>
              </FilterComponent>
            </Box>
          </Grid>
        </Paper>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 50, whiteSpace: "nowrap" }}>
                    ID
                  </TableCell>
                  <TableCell sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                    Question No.
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Question Type
                  </TableCell>
                  <TableCell sx={{ minWidth: 60, whiteSpace: "nowrap" }}>
                    Marks
                  </TableCell>
                  <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap" }}>
                    Section Title
                  </TableCell>
                  <TableCell sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                    Section Order
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Subject
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Chapter
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Topic
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Created By
                  </TableCell>
                  <TableCell sx={{ minWidth: 160, whiteSpace: "nowrap" }}>
                    Created At
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Updated By
                  </TableCell>
                  <TableCell sx={{ minWidth: 160, whiteSpace: "nowrap" }}>
                    Updated At
                  </TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {examPaper.map((question: any) => (
                  <TableRow key={question.id}>
                    {[
                      question.id,
                      question.questionNumber,
                      question.questionType,
                      question.marks,
                      question.sectionTitle,
                      question.sectionOrder,
                      question.subjectName,
                      question.chapterName,
                      question.topicName,
                      question.createdByName,
                      new Date(question.createdAt).toLocaleString(),
                      question.updatedByName,
                      new Date(question.updatedAt).toLocaleString(),
                    ].map((value, idx) => (
                      <TableCell
                        key={idx}
                        sx={{
                          maxWidth: 150,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <Tooltip title={value || ""} arrow>
                          <span>{value}</span>
                        </Tooltip>
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          history.push(
                            `/ai-evaluation/exam-paper/update-questions/${question.id}`
                          );
                        }}
                      >
                        Update
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <div className={classes.paginationWrapper}>
          <TablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => handleChangePage(e, newPage, setPage)}
            onRowsPerPageChange={(e: any) =>
              handleChangeRowsPerPage(e, setRowsPerPage, setPage)
            }
            rowsPerPageOptions={[]}
            component="div"
          />
        </div>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
      >
        <Alert
          onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
          severity={
            snackbar.severity as
              | "error"
              | "info"
              | "success"
              | "warning"
              | undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default ViewExamPaperQuestions;
